import { useCallback, useEffect, useState } from "react";

import {
  fetchPatientInsuranceImages,
  fetchReferralInsuranceImages,
} from "@/utils/insuranceImageHelpers";
import { PATIENT_INSURANCE_IMAGE_FILE_TYPE } from "@/utils/fileUtil";

/**
 * React hook to fetch and manage insurance images for patients or referrals
 * @param {Object} entity - Patient or referral object
 * @param {string} fileType - File type constant (default: PATIENT_INSURANCE_IMAGE_FILE_TYPE)
 * @param {boolean} isReferral - Whether entity is a referral (default: false)
 * @returns {Object} { imageUrls, imageTypes, imageStatuses, loading, error, reload }
 */
export const useInsuranceImages = (
  entity,
  fileType = PATIENT_INSURANCE_IMAGE_FILE_TYPE,
  isReferral = false,
) => {
  const [imageUrls, setImageUrls] = useState({
    pbUrl: null,
    pfUrl: null,
    sbUrl: null,
    sfUrl: null,
  });

  const [imageTypes, setImageTypes] = useState({
    pbType: null,
    pfType: null,
    sbType: null,
    sfType: null,
  });

  const [imageStatuses, setImageStatuses] = useState({
    pbStatus: null,
    pfStatus: null,
    sbStatus: null,
    sfStatus: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchImages = useCallback(
    async (entityData) => {
      if (!entityData) return;

      setLoading(true);
      setError(null);

      try {
        const result = isReferral
          ? await fetchReferralInsuranceImages(entityData)
          : await fetchPatientInsuranceImages(entityData, fileType);

        setImageUrls(result.imageUrls);
        setImageTypes(result.imageTypes);
        setImageStatuses(result.imageStatuses);
      } catch (err) {
        setError(err);
        console.error("Failed to fetch insurance images:", err);
      } finally {
        setLoading(false);
      }
    },
    [fileType, isReferral],
  );

  useEffect(() => {
    fetchImages(entity);
  }, [entity, fetchImages]);

  return {
    error,
    imageStatuses,
    imageTypes,
    imageUrls,
    loading,
    reload: fetchImages,
  };
};
